/**
 * Sueldos pactados (0156): cuánto cobra cada socio por mes.
 *
 * Con esto un «Sueldo de socio» no pasa lo pactado del mes: en noviembre se
 * cargaron como sueldo el cloro, el agua y la ferretería. Sin sueldo pactado,
 * no se carga sueldo de socio.
 */
import { useState } from "react";

import { InputImporte } from "@/components/money/input-importe";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Segmented } from "@/components/ui/segmented";
import { aNumero, type Socio } from "@/lib/tipos";

export interface SueldoPactado {
  id: number;
  sueldo_importe: number | null;
  sueldo_moneda: "ARS" | "USD" | null;
}

export interface DialogSueldosProps {
  abierto: boolean;
  onCerrar: () => void;
  socios: ReadonlyArray<Socio>;
  guardando?: boolean;
  onGuardar: (s: SueldoPactado) => void;
}

export function DialogSueldos({
  abierto,
  onCerrar,
  socios,
  guardando = false,
  onGuardar,
}: DialogSueldosProps): JSX.Element {
  return (
    <Dialog open={abierto} onOpenChange={(a) => !a && onCerrar()}>
      <DialogContent tamano="sm" mobile="hoja">
        <DialogHeader>
          <DialogTitle>Sueldos pactados</DialogTitle>
          <DialogDescription>
            Cuánto cobra cada socio por mes. Un sueldo de socio que pasa lo pactado del mes no se
            carga: lo que se paga para el local va en Caja, y lo que se lleva un socio es un retiro.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-4">
            {socios
              .filter((s) => s.activo)
              .map((s) => (
                <FilaSueldo key={s.id} socio={s} guardando={guardando} onGuardar={onGuardar} />
              ))}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCerrar}>
            Listo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FilaSueldo({
  socio,
  guardando,
  onGuardar,
}: {
  socio: Socio;
  guardando: boolean;
  onGuardar: (s: SueldoPactado) => void;
}): JSX.Element {
  const [importe, setImporte] = useState<number | null>(
    socio.sueldo_importe != null ? aNumero(socio.sueldo_importe) : null,
  );
  const [moneda, setMoneda] = useState<"ARS" | "USD">(socio.sueldo_moneda ?? "ARS");
  const cambio =
    importe !== (socio.sueldo_importe != null ? aNumero(socio.sueldo_importe) : null) ||
    (importe !== null && moneda !== (socio.sueldo_moneda ?? "ARS"));

  return (
    <div className="space-y-2">
      <Label>{socio.nombre}</Label>
      <Segmented
        etiqueta={`Moneda del sueldo de ${socio.nombre}`}
        apariencia="chips"
        tamano="sm"
        opciones={[
          { valor: "ARS", etiqueta: "En pesos" },
          { valor: "USD", etiqueta: "En dólares" },
        ]}
        valor={moneda}
        onChange={(v) => setMoneda(v as "ARS" | "USD")}
      />
      <InputImporte
        label={`Sueldo por mes de ${socio.nombre}`}
        valor={importe}
        onValorChange={setImporte}
        moneda={moneda}
      />
      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          variant="outline"
          cargando={guardando}
          disabled={!cambio}
          onClick={() =>
            onGuardar({
              id: socio.id,
              sueldo_importe: importe,
              sueldo_moneda: importe === null ? null : moneda,
            })
          }
        >
          Guardar
        </Button>
      </div>
    </div>
  );
}
